// Daily and weekly quests and the weekly chest: a compact card on the hunt's
// left rail and the full Quests page. Both only read the trainer state; quests
// move on the server with every kill, catch, boss win and Arena battle, and the
// Claim button is the only action. Rules live in Help (client/ui.ts wikiFeatures).
import { psecTabs, psecWrap } from './psec.ts';
import { ITEM } from '../shared/items.ts';
import { REGIONS } from '../shared/grounds.ts';
import {
  claimable, questDone, questStage, questText, questSets, questRewards, bonusOf, rewardLabel, addBundle, emptyBundle, chestReached,
  CHEST, CHEST_MAX_MARKS, type Bundle, type Quest, type QuestDay, type Reward,
} from '../shared/quests.ts';
import { S, serverNow } from './state.ts';
import { ICON } from './icons.ts';
import { iconUrl } from './assets.ts';
import { esc, full, left, num } from './fmt.ts';
import { patch } from './dom.ts';
import { rerollsLeft } from '../shared/pass.ts';
import { CONFIG } from '../shared/config.ts';

const regionName = (r: string) => REGIONS.find((x) => x.id === r)?.name ?? r;
const itemName = (id: string) => ITEM[id]?.name ?? id;

/** Everything waiting across today, yesterday, this week and last week. */
export function questsWaiting(): Bundle & { count: number } {
  const out = { ...emptyBundle(), count: 0 };
  for (const d of questSets(S.trainer?.quests)) { const c = claimable(d); out.count += c.count; addBundle(out, c); }
  return out;
}

/** Time to the next UTC midnight, to the next UTC Monday for the week, or to the next UTC hour. */
function untilReset(weekly = false, hourly = false) {
  const n = serverNow(), d = new Date(n);
  if (hourly) return left(Math.ceil((n + 1) / 3_600_000) * 3_600_000 - n);
  const days = weekly ? 7 - ((d.getUTCDay() + 6) % 7) : 1;
  return left(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days) - n);
}

/** Icon for a reward line: item art, or the coin, diamond, XP boost, Tournament Ticket and coin boost icons. */
const rewardIcon = (r: Reward) => iconUrl(r.k === 'item' ? r.item ?? 'items' : r.k === 'coins' ? 'coin' : r.k === 'diamonds' ? 'diamond' : r.k === 'xpBoost' ? 'boost-xp' : r.k === 'ticket' ? 'ticket' : 'boost-coin');
/** Short amount beside the icon: 12.4k coins, 30m or 1h of boost, ×10 seals. */
const rewardAmount = (r: Reward) => (r.k === 'xpBoost' || r.k === 'coinBoost' || r.k === 'auraCharm' ? (r.n % 60 ? `${r.n}m` : `${r.n / 60}h`) : r.k === 'item' || r.k === 'ticket' ? `×${r.n}` : num(r.n));

/** One reward as an icon and amount; the full name is the tooltip. */
export function rewardChip(r: Reward, cls = '') {
  const label = rewardLabel(r, itemName);
  return `<span class="rw${r.k === 'diamonds' ? ' dia' : ''}${cls}" title="${esc(label)}">${r.k === 'auraCharm' ? ICON.aura : `<img src="${rewardIcon(r)}" alt="">`}${esc(rewardAmount(r))}<span class="sr">${esc(label)}</span></span>`;
}

const bundleLines = (b: Bundle): Reward[] => [
  ...(b.coins ? [{ k: 'coins' as const, n: b.coins }] : []),
  ...Object.entries(b.items).map(([item, n]) => ({ k: 'item' as const, item, n })),
  ...(b.xpBoost ? [{ k: 'xpBoost' as const, n: b.xpBoost }] : []),
  ...(b.coinBoost ? [{ k: 'coinBoost' as const, n: b.coinBoost }] : []),
  ...(b.diamonds ? [{ k: 'diamonds' as const, n: b.diamonds }] : []),
  ...(b.ticket ? [{ k: 'ticket' as const, n: b.ticket }] : []),
  ...(b.auraCharm ? [{ k: 'auraCharm' as const, n: b.auraCharm }] : []),
];

function claimBtn(cls = '') {
  const w = questsWaiting();
  return w.count ? `<button class="btn gold small q-claimbtn${cls}" data-act="quest-claim"><b>Claim</b>${bundleLines(w).map((r) => rewardChip(r)).join('')}</button>` : '';
}

/** The rail card: today's three thin bars, the weekly chest's track, and a Claim button when something is finished. */
export function renderQuestSummary() {
  const host = document.getElementById('questsum');
  const q = S.trainer?.quests;
  if (!host) return;
  if (!q) { patch(host, ''); host.hidden = true; return; }
  host.hidden = false;
  const done = q.today.list.filter(questDone).length;
  const wk = q.week;
  patch(host, `<div class="qs-head"><b>Daily quests</b><span class="muted">${done} of ${q.today.list.length}</span><span class="spacer"></span><a class="link" href="#/quests">All quests</a></div>
    ${q.today.list.map((x) => `<div class="qs-row${questDone(x) ? ' done' : ''}" title="${esc(questText(x, regionName))}"><span class="ellip">${questDone(x) ? '✓ ' : ''}${esc(questText(x, regionName))}</span><span class="bar thin"><i style="width:${(x.progress / x.need) * 100}%"></i></span></div>`).join('')}
    ${q.hour ? q.hour.list.map((x) => `<div class="qs-row qs-hour${questDone(x) ? ' done' : ''}" title="${esc(questText(x, regionName))}"><span class="ellip"><b>Hourly</b> ${questDone(x) ? '✓ ' : ''}${esc(questText(x, regionName))}</span>${goBtn(x) || '<span></span>'}<span class="bar thin"><i style="width:${(x.progress / x.need) * 100}%"></i></span></div>`).join('') : ''}
    ${wk ? `<div class="qs-wkhead"><b>Weekly</b><span class="muted">${wk.list.filter(questDone).length} of ${wk.list.length}</span></div>${wk.list.map((x) => `<div class="qs-row qs-wk${questDone(x) ? ' done' : ''}" title="${esc(questText(x, regionName))}"><span class="ellip">${questDone(x) ? '✓ ' : ''}${esc(questText(x, regionName))}</span><span class="bar thin"><i style="width:${(x.progress / x.need) * 100}%"></i></span></div>`).join('')}` : ''}
    ${wk ? `<a class="qs-chest" href="#/quests"><span>Weekly chest</span>${chestMini(wk)}<span class="muted">${wk.marks ?? 0}/${CHEST_MAX_MARKS}</span></a>` : ''}
    ${claimBtn()}`);
}

/** The rail's chest line: a thin bar with a small mark at each step (gilt when reached, wax once claimed). */
function chestMini(w: QuestDay) {
  const reached = chestReached(w), claimed = w.chest ?? 0;
  const pct = (n: number) => `${(n / CHEST_MAX_MARKS) * 100}%`;
  return `<span class="qs-chestbar"><span class="bar thin"><i style="width:${pct(w.marks ?? 0)}"></i></span>${CHEST.map((s, i) => `<b class="tick ${i < claimed ? 'claimed' : i < reached ? 'ready' : 'locked'}" style="left:${pct(s.at)}"></b>`).join('')}</span>`;
}

/** The chest track: a bar with a node per step; each node shows the step's rewards. */
function chestTrack(w: QuestDay) {
  const marks = w.marks ?? 0, reached = chestReached(w), claimed = w.chest ?? 0;
  const pct = (n: number) => `${(n / CHEST_MAX_MARKS) * 100}%`;
  const nodes = CHEST.map((s, i) => {
    const st = i < claimed ? 'claimed' : i < reached ? 'ready' : 'locked';
    const rw = s.rewards(w.chestLevel ?? 1);
    const tip = `${s.at} marks: ${rw.map((r) => rewardLabel(r, itemName)).join(', ')}`;
    return `<li class="cn ${st}" style="left:${pct(s.at)}" title="${esc(tip)}"><i class="seal">${i < claimed ? '✓' : s.at}</i><span class="cn-rw">${rw.map((r) => rewardChip(r)).join('')}</span></li>`;
  }).join('');
  return `<div class="chest"><span class="ch-bar"><i style="width:${pct(marks)}"></i></span><ol class="ch-nodes">${nodes}</ol></div>`;
}

/** The season pass's daily redraw: one left today, and a kind the day doesn't have yet to draw. */
function canRedraw(d: QuestDay) {
  const t = S.trainer!;
  return rerollsLeft(t, serverNow(), d.day) > 0 && questStage(t.level).kinds.some((k) => !d.list.some((x) => x.kind === k));
}

/** A small Go button that switches to the hunt an hourly quest asks for, unless the trainer is already there. */
function goBtn(x: Quest) {
  if (!x.ground || questDone(x) || S.trainer?.ground === x.ground) return '';
  return `<button class="btn tiny q-go" data-act="switch-hunt" data-id="${esc(x.ground)}" title="Go to this hunt">Go</button>`;
}

function questRow(x: Quest, redraw = -1) {
  const done = questDone(x);
  const re = redraw >= 0 && !done && !x.claimed ? `<button class="iconbtn q-redraw" data-act="pass-reroll" data-id="${redraw}" title="Redraw" aria-label="Redraw this quest">↻</button>` : '';
  const state = x.claimed ? '<span class="q-st good">✓</span>' : done ? '<span class="q-st ready">Done</span>' : `<span class="q-st">${full(x.progress)} / ${full(x.need)}</span>`;
  return `<li class="q-row${done ? ' done' : ''}${x.claimed ? ' claimed' : ''}">
    <div class="q-main"><b>${esc(questText(x, regionName))}</b>${re}${goBtn(x)}${state}</div>
    <span class="bar q-bar"><i style="width:${(x.progress / x.need) * 100}%"></i></span>
    <div class="q-reward">${questRewards(x).map((r) => rewardChip(r)).join('')}</div></li>`;
}

function dayBlock(d: QuestDay, title: string) {
  const q = S.trainer!.quests!;
  const all = d.list.every(questDone);
  const current = d === q.today || d === q.week || d === q.hour;
  const when = current ? `New in ${untilReset(d.weekly, d.hourly)}` : d.weekly ? 'Claim before this week ends' : d.hourly ? 'Claim before this hour ends' : 'Claim before today ends';
  const bonus = bonusOf(d);
  return `<section class="panel q-day${d.weekly ? ' q-week' : ''}${d.hourly ? ' q-hour' : ''}"><div class="cp-h"><h4>${title}</h4><span class="spacer"></span><span class="muted">${when}</span></div>
    <ol class="q-list">${d.list.map((x, i) => questRow(x, d === q.today && canRedraw(d) ? i : -1)).join('')}</ol>
    ${bonus ? `<p class="q-bonus${all ? ' done' : ''}"><span>All three</span>${rewardChip({ k: 'diamonds', n: bonus })}${d.bonusClaimed ? '<span class="q-st good">✓</span>' : `<span class="q-st">${d.list.filter(questDone).length} / ${d.list.length}</span>`}</p>` : ''}
    ${d.weekly ? `<div class="q-chest"><div class="q-chest-h"><b>Weekly chest</b><span class="muted">${d.marks ?? 0} / ${CHEST_MAX_MARKS} marks</span></div>${chestTrack(d)}</div>` : ''}</section>`;
}

/** A finished set (yesterday, last week, last hour) as one line: what still waits there. The Claim button above takes it. */
function pastStrip(d: QuestDay | undefined, title: string) {
  const c = d ? claimable(d) : null;
  if (!c?.count) return '';
  const when = d!.weekly ? 'claim before this week ends' : d!.hourly ? 'claim before this hour ends' : 'claim before today ends';
  return `<p class="q-past"><b>${title}</b>${bundleLines(c).map((r) => rewardChip(r)).join('')}<span class="muted">${when}</span></p>`;
}

/** Today's daily reward as a Claim button, while it waits (UTC day, like the gift menu). */
function dailyBtn() {
  const t = S.trainer!;
  const today = new Date().toISOString().slice(0, 10), yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  if (t.daily.last === today) return '';
  const days = CONFIG.dailyClaimDiamonds;
  const day = ((t.daily.last === yesterday ? t.daily.streak + 1 : 1) - 1) % days.length;
  const rw: Reward[] = [{ k: 'item', item: 'rune-seal', n: 10 }, ...(days[day] ? [{ k: 'diamonds' as const, n: days[day] }] : [])];
  return `<button class="btn gold small q-claimbtn" data-act="daily"><b>Daily reward · day ${day + 1}</b>${rw.map((r) => rewardChip(r)).join('')}</button>`;
}

export function questsPane() {
  const q = S.trainer?.quests;
  if (!q) return '';
  const claim = dailyBtn() + claimBtn();
  const keys = ['hour', 'today', 'week'];
  const ready = (...ds: (typeof q.today | undefined)[]) => ds.some((d) => d && claimable(d).count > 0);
  const hour = `${q.hour ? dayBlock(q.hour, 'This hour') : ''}${pastStrip(q.prevHour, 'Last hour')}`;
  return `${claim ? `<div class="q-claim">${claim}</div>` : ''}
    ${psecTabs('quests', [
      ...(hour ? [{ key: 'hour', title: 'This hour', dot: ready(q.hour, q.prevHour) }] : []),
      { key: 'today', title: 'Today', dot: ready(q.today, q.prev) },
      ...(q.week ? [{ key: 'week', title: 'This week', dot: ready(q.week, q.prevWeek) }] : []),
    ])}
    <div class="q-cols">
      <div>${hour ? psecWrap('quests', 'hour', keys, hour) : ''}
        ${psecWrap('quests', 'today', keys, `${dayBlock(q.today, 'Today')}${pastStrip(q.prev, 'Yesterday')}`)}</div>
      <div>${q.week ? psecWrap('quests', 'week', keys, `${dayBlock(q.week, 'This week')}${pastStrip(q.prevWeek, 'Last week')}`) : ''}</div>
    </div>`;
}
